import React, { useState, useEffect, useContext, } from 'react';
import { Form, Space, Button, Table, Drawer, Row, Col, Alert, Tooltip, } from 'antd';
import { EditOutlined, WarningFilled } from '@ant-design/icons';
import { EditSerialNumberModal } from '../../Modals/EditSerialNumberModal.component';
import { getDeviceTitle } from './DevicesList/DevicesList.component';
import { StationContext } from '../../../../context/station-context';
import { PermissionsContext } from '../../../../router/DashboardRoutes';

export const SerialNumCell = ({panelPending, value}) => {

    return (
        panelPending ? 
            <Space>
                <Tooltip title='Displaying pending changes'>
                    <WarningFilled style={{color: 'var(--warning-color)'}}/>
                </Tooltip>
                {value}
            </Space> 
        :
            <p>{value}</p>
    )
}

export const GroupedDevicesTable = ({ dataSource, columns, serialNum, formItems, editable, ...props}) => {

    const { isPanelPending, saveChanges, currentDevice, setCurrentDevice, } = props;

    const [form] = Form.useForm();

    const userPermissions = useContext(PermissionsContext);
    const allowEditDevice = userPermissions.device.edit;

    const [stationState, stationDispatch] = useContext(StationContext);
    const { pendingQueue } = stationState;

    const [displayModal, setDisplayModal] = useState({display: false, id: '', serialNum: ''});
    const [displayDrawer, setDisplayDrawer] = useState(false);
    const [errors, setErrors] = useState([]);

    const editColumn = {
        title: '',
        dataIndex: 'edit-action',
        key: 'edit-action',
        width: 30,
        render: (_, record) => {
            return (
                <Tooltip title='Edit'>
                    <Button 
                        icon={<EditOutlined/>}
                        type='link'
                        onClick={() =>  editDevice(record)}
                        />
                </Tooltip>
            );
        }
    }

    const renderColumns = () => {
        if(allowEditDevice && editable && !columns.some(column => column.dataIndex === 'edit-action')){
            columns.unshift(editColumn)
        }
        return columns;
    }

    useEffect(() => {
        if(displayDrawer){
            form.resetFields();
            setErrors([]);
        }
    }, [displayDrawer, form])

    useEffect(() => {
        form.setFieldsValue(currentDevice);
    }, [currentDevice])

    const editSerialNum = (id, serialNum) => {
        setDisplayModal({display: true, id: id, serialNum: serialNum});
    } 

    const editDevice = (record) => {
        setDisplayDrawer(true);

        if(isPanelPending(record)) {
            setCurrentDevice(pendingQueue.find(device => device.id === record.id))
        } else {
            setCurrentDevice(record);
        }
    }

    const closeHandler = () => {
        form.resetFields();
        setDisplayDrawer(false);
    }

    const valuesChangeHandler = (changedValues, allValues) => {

        let editedDevice = {...currentDevice, ...allValues}

        stationDispatch({
            type: 'ADD_PENDING_QUEUE', 
            payload: editedDevice,
        })
    }

    const discardChanges = () => {
        stationDispatch({
            type: 'REMOVE_PENDING_CHANGE', 
            payload: currentDevice.id,
        })
        setDisplayDrawer(false);
    }

    const saveHandler = () => {
        saveChanges([currentDevice.id])
        if(!pendingQueue.includes(currentDevice.id)){
            setDisplayDrawer(false);
        }
    }

    const onFieldsChange = (changedFields, allFields) => {
        const errorFields = allFields.filter((field) => field.errors.length > 0);
        setErrors(errorFields);
    }

    return (
        <>
            <Table
                columns={renderColumns()}
                dataSource={dataSource} 
                rowClassName={(record) => (isPanelPending(record) && allowEditDevice) && 'row__edited'} 
                rowKey='id'
                size='small'
                scroll={{
                        x: (dataSource?.length > 0 && columns.length > 5 ) ? true : false,
                    }}
            />

            {displayDrawer && currentDevice && allowEditDevice && 
                <Drawer
                    title="Edit Device"
                    placement="right"
                    closable={!errors.length > 0}
                    maskClosable={!errors.length > 0}
                    onClose={() => setDisplayDrawer(false)}
                    getContainer={false}
                    open={displayDrawer}
                    rootStyle={{ 
                        height: '100%', 
                        position: 'fixed', 
                        top: 0, 
                        bottom: 0 
                    }}
                    >
                    <Form 
                        autoComplete='off'
                        form={form}
                        name={`${currentDevice?.deviceType}-${currentDevice.id}-device-form`}
                        layout='vertical'
                        initialValues={currentDevice}
                        onValuesChange={(changedValues, allValues) => valuesChangeHandler(changedValues, allValues)}
                        onFinish={() => {saveHandler()}}
                        onFieldsChange={onFieldsChange}
                        >

                        { isPanelPending(currentDevice) &&
                            <>
                                <Form.Item>
                                    <Alert type='warning' showIcon description='You have unsaved changes'/>
                                </Form.Item>
                            </>
                        }

                        { serialNum && 
                            <Form.Item label="Serial Number:" name='serialNum' tooltip={isPanelPending(currentDevice) ? "Serial number can't be updated. Pending changes must be cleared." : null}>
                                <Button
                                    disabled={isPanelPending(currentDevice) || !allowEditDevice} 
                                    onClick={() => !isPanelPending(currentDevice) ? editSerialNum(currentDevice.id, currentDevice.serialNum) : undefined}
                                    style={{
                                        width: '100%', 
                                        display: 'flex', 
                                        justifyContent: 'flex-start', 
                                        alignItems: 'center'
                                    }} 
                                    >
                                    <Space>
                                        {currentDevice.serialNum}
                                        <EditOutlined/>
                                    </Space>
                                </Button>
                            </Form.Item>
                        }
                    
                        { formItems }

                        <Form.Item>
                            <Row justify='end' wrap={true} gutter={[8,8]}>
                                <Col>
                                    <Space>
                                        <Button htmlType='submit' disabled={!isPanelPending(currentDevice)} color='yellow' variant='solid'>Save Changes</Button>
                                        
                                        {isPanelPending(currentDevice) &&
                                            <Button onClick={() => discardChanges()}>Discard Changes</Button>
                                        }
                                    
                                    </Space>
                                </Col>
                                <Col>
                                    <Button disabled={errors.length > 0} onClick={() => errors.length > 0 ? undefined : closeHandler()}>Close</Button>
                                </Col>
                            </Row>
                        </Form.Item>
                    </Form>
                </Drawer>
            }

            { displayModal.display && 
                <EditSerialNumberModal 
                    visible={displayModal.display} 
                    setVisible={setDisplayModal} 
                    deviceType={getDeviceTitle(currentDevice.deviceType)} 
                    displayField={false} 
                    id={displayModal.id} 
                    serialNum={displayModal.serialNum} 
                    setEditDeviceDrawer={setDisplayDrawer}
                    />
            }
        </>
    );
}

GroupedDevicesTable.defaultProps = {
    dataSource: [], 
    columns: [], 
    serialNum: true,
    formItems: '', 
    editable: true,
}
